import { useEffect, useState } from 'react';
import { Form } from '@bedrock/components';
import { fetchRechargeDraft, fetchRechargeDetail } from '../rechargeService';
import { buildRechargeInitialValues, buildRechargeEditValues } from './buildValues';
import type {
  RechargeDetailDto,
  RechargeOrderDetailVo,
  RechargeEntryEnum,
} from '#services/api';

type FormInstance = ReturnType<typeof Form.useForm>[0];

interface UseRechargeDraftParams {
  l2_demand_id: number;
  l3_demand_id?: number;
  recharge_order_id?: number;
  entry: RechargeEntryEnum;
}

type UseRechargeDraftDetail = RechargeDetailDto | RechargeOrderDetailVo;

/**
 * 加载充值草稿（draft）：返回预填数据 + 核算预判（is_early_recharge）。
 * visible 时调用，自动 setFieldsValue；前置校验失败（code !== 1 / HTTP 400）提示"不可发起充值"。
 * is_early_recharge 与金额无关，draft 拿一次回调给 calc 状态。
 * <p>
 * 命令式发起（方案A）：调用方 show 前先调 fetchRechargeDraft 拿到外部草稿，通过 {@code externalDetail}
 * 透传过来。传了 externalDetail 时**跳过网络请求**，仅执行预填/calc 映射（避免"调用方校验 + 组件内拉草稿"调两次）；
 * 未传时（resubmit/受控路径）维持原网络拉取。
 */
export function useRechargeDraft(
  form: FormInstance | undefined,
  params: UseRechargeDraftParams,
  visible: boolean,
  onCalcReady?: (calc: { is_over_charge?: boolean; is_early_recharge?: boolean }) => void,
  onError?: (msg: string) => void,
  externalDetail?: UseRechargeDraftDetail
) {
  const [detail, setDetail] = useState<UseRechargeDraftDetail>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 校验通过前置条件不满足时直接返回（不拉草稿）；统一返回 cleanup/undefined 满足 TS 7030
    if (!visible || !form) return undefined;
    let active = true;
    setLoading(true);
    setDetail(undefined);

    // 命令式发起（外部已调草稿，透传 detail）：跳过网络，仅预填 + calc 映射。
    // create 模式走 buildRechargeInitialValues；resubmit（recharge_order_id）走 buildRechargeEditValues。
    if (externalDetail != null) {
      setDetail(externalDetail);
      form.setFieldsValue(
        params.recharge_order_id != null
          ? buildRechargeEditValues(externalDetail as RechargeOrderDetailVo)
          : buildRechargeInitialValues(externalDetail as RechargeDetailDto)
      );
      onCalcReady?.({
        is_over_charge: (externalDetail as any)?.is_over_charge,
        is_early_recharge: (externalDetail as any)?.is_early_recharge,
      });
      setLoading(false);
      return () => {
        active = false;
      };
    }

    // 待发起态编辑（退回后重新发起）：走 detail 接口回填详情可编辑（含上次填写内容 + 核算结果）
    if (params.recharge_order_id != null) {
      fetchRechargeDetail({ order_id: params.recharge_order_id }, { alertError: false })
        .then((res) => {
          if (!active) return;
          if (res?.code === 1 && res.data) {
            setDetail(res.data);
            form.setFieldsValue(buildRechargeEditValues(res.data));
            onCalcReady?.({ is_over_charge: res.data.is_over_charge, is_early_recharge: res.data.is_early_recharge });
          } else {
            onError?.(res?.message || '加载充值单详情失败');
          }
        })
        .catch((err) => {
          if (!active) return;
          onError?.(err?.message || '加载充值单详情失败');
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
      };
    }

    // 首次发起：走 draft 接口预填 + 核算预判
    fetchRechargeDraft(params)
      .then((res) => {
        if (!active) return;
        if (res?.code === 1 && res.data) {
          setDetail(res.data);
          form.setFieldsValue(buildRechargeInitialValues(res.data));
          onCalcReady?.({ is_over_charge: res.data.is_over_charge, is_early_recharge: res.data.is_early_recharge });
        } else {
          onError?.(res?.message || '当前单据不可发起充值');
        }
      })
      .catch((err) => {
        if (!active) return;
        onError?.(err?.message || '当前单据不可发起充值');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, params.l2_demand_id, params.l3_demand_id, params.recharge_order_id]);

  return { detail, loading };
}
